import axios from 'axios';

// Use environment variable for API URL, fallback to localhost for development
// In Vite, use import.meta.env instead of process.env
// Environment variables must be prefixed with VITE_ to be exposed to the client
const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000/api/';

// Log API URL in development mode for debugging
if (import.meta.env.DEV) {
  console.log('🔧 Development Mode - API Base URL:', API_BASE_URL);
  console.log('📝 Environment variable VITE_API_URL:', import.meta.env.VITE_API_URL || 'not set (using default)');
} else {
  // Log in production for debugging
  console.log('🌐 Production Mode - API Base URL:', API_BASE_URL);
}

const API = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 10000, // 10 second timeout
  withCredentials: true,
  xsrfCookieName: 'csrftoken',
  xsrfHeaderName: 'X-CSRFToken',
});

API.interceptors.request.use((config) => {
  // Ensure baseURL is properly set
  if (!config.url.startsWith('http') && !config.baseURL) {
    console.warn('⚠️ API request missing baseURL:', config.url);
  }
  
  return config;
});

// Handle token refresh on 401 errors and log errors for debugging
API.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    
    // Log error details for debugging
    if (error.response) {
      console.error('❌ API Error:', {
        url: originalRequest.url,
        method: originalRequest.method,
        status: error.response.status,
        statusText: error.response.statusText,
        data: error.response.data,
        fullURL: `${API_BASE_URL}${originalRequest.url}`
      });
    } else if (error.request) {
      console.error('❌ API Request Error (no response):', {
        url: originalRequest.url,
        method: originalRequest.method,
        fullURL: `${API_BASE_URL}${originalRequest.url}`
      });
    } else {
      console.error('❌ API Error:', error.message);
    }
    
    // If error is 401 and we haven't already retried
    const isAuthRequest = originalRequest.url?.includes('auth/login/')
      || originalRequest.url?.includes('auth/register/')
      || originalRequest.url?.includes('auth/refresh/');

    if (error.response?.status === 401 && !originalRequest._retry && !isAuthRequest) {
      originalRequest._retry = true;
      
      try {
        await axios.post(`${API_BASE_URL}auth/refresh/`, {}, { withCredentials: true });
        return API(originalRequest);
      } catch (refreshError) {
        window.dispatchEvent(new Event('auth-session-ended'));
        // Refresh failed. The calling route decides whether to show or navigate
        // to login; forcing a page reload here creates an authentication loop.
        return Promise.reject(refreshError);
      }
    }
    
    return Promise.reject(error);
  }
);

export default API;
export const registerUser = async (data) => {
  return await API.post('auth/register/', data);
};

export const loginUser = async (data) => {
  return await API.post('auth/login/', data);
};

export const logoutUser = async () => {
  const response = await API.post('auth/logout/');
  window.dispatchEvent(new Event('auth-session-ended'));
  return response;
};

export const getUser = async () => {
  return await API.get('auth/user/');
};

export const checkAvailability = async (date, roomIds) => {
  const roomIdsStr = Array.isArray(roomIds) ? roomIds.join(',') : roomIds;
  return await API.get(`check_availability/?date=${date}&room_ids=${roomIdsStr}`);
};

export const getAvailableRooms = async (date, startHour, endHour) => {
  return await API.get('available_rooms/', {
    params: {
      date,
      start_hour: startHour,
      end_hour: endHour,
    },
  });
};

export const getCampBookingWarnings = async () => {
  return await API.get('camp-booking-warnings/');
};

export const createBooking = async (data) => {
  return await API.post('create_booking/', data);
};

export const getFloors = async () => {
  return await API.get('floors/');
};

export const getRooms = async (floorId = null) => {
  const url = floorId ? `rooms/?floor=${floorId}` : 'rooms/';
  return await API.get(url);
};

export const getMyBookings = async () => {
  return await API.get('bookings/my');
};

export const getBooking = async (bookingId) => {
  return await API.get(`bookings/${bookingId}/`);
};

export const updateBooking = async (bookingId, data) => {
  return await API.put(`bookings/${bookingId}/`, data);
};

export const deleteBooking = async (bookingId) => {
  return await API.delete(`bookings/${bookingId}/`);
};

export const getAllBookings = async () => {
  return await API.get('bookings/');
};

export const getPendingUsers = async () => {
  return await API.get('admin/pending-users/');
};

export const approveUser = async (userId, action, role = null) => {
  return await API.post(`admin/approve-user/${userId}/`, { action, role });
};

export const updateBookingStatus = async (bookingId, status) => {
  return await API.post(`admin/bookings/${bookingId}/status/`, { status });
};

export const deleteAllBookings = async () => {
  return await API.delete('admin/bookings/delete-all/');
};

export const requestPasswordReset = async (data) => {
  return await API.post('auth/password-reset/', data);
};

export const confirmPasswordReset = async (data) => {
  return await API.post('auth/password-reset-confirm/', data);
};
